import { isEmpty, isNil, partition } from 'lodash/fp';
import { DateTime } from 'luxon';
import { DateBin, ROI_CHART_KEY } from 'app/lib/constants';
import { splitFqn } from 'app/lib/utils';
import { OPS } from 'app/lib/utils/sql';
import { transformSnowflakeResponse } from 'app/store/snowflake/transformer.js';

const dateFormat = {
    YEAR: 'yyyy',
    MONTH: 'yyyy-MM',
    WEEK: 'kkkk-WW',
    DAY: 'yyyy-MM-dd',
    HOUR: 'yyyy-MM-dd HH'
};

export function sumObjectValues(arrayOfObjects, bin, key) {
    const sumObject = {};
    arrayOfObjects.forEach((obj) => {
        Object.keys(obj).forEach((field) => {
            if (field === 'TABLE_FQN' || field === 'ACCESS_TYPE') {
                sumObject[field] = obj[field];
                return;
            }
            if (field === 'TIMESTAMP') {
                if (bin === DateBin.WEEKDAY) {
                    sumObject.WEEKDAY = key;
                } else {
                    sumObject[field] = obj[field].startOf(bin.toLowerCase());
                }
                return;
            }
            if (!sumObject[field]) {
                sumObject[field] = 0;
            }
            sumObject[field] += isNil(obj[field]) ? 0 : Number(obj[field]);
        });
    });

    return sumObject;
}

const getKey = (date, bin) => {
    const isWeekday = bin === DateBin.WEEKDAY;
    const isQuarter = bin === DateBin.QUARTER;
    return isQuarter
        ? `${date.year}-${date.quarter}`
        : isWeekday
          ? date.weekday
          : date.toFormat(dateFormat[bin]);
};

export function getAggregatedDataByBin(data, bin) {
    if (!bin) {
        return null;
    }
    const aggregatedData = {};
    data?.forEach((record) => {
        const date = record.TIMESTAMP;
        const key = getKey(date, bin);
        if (!aggregatedData[key]) {
            aggregatedData[key] = [];
        }
        aggregatedData[key].push(record);
    });
    return Object.entries(aggregatedData).map(([key, values]) => sumObjectValues(values, bin, key));
}

const isNoData = (sum, count) => isNil(sum) || isNil(count) || Number(count) === 0;

export const getMetric = (key, entry) => {
    switch (key) {
        case ROI_CHART_KEY.AVG_QUERY_COUNT: {
            const { ACCESS_COUNT_SUM, ACCESS_COUNT_COUNT } = entry;
            const noData = isNoData(ACCESS_COUNT_SUM, ACCESS_COUNT_COUNT);
            return noData ? 0 : Math.round(ACCESS_COUNT_SUM / ACCESS_COUNT_COUNT);
        }
        case ROI_CHART_KEY.AVG_QUERY_TIME: {
            const { ELAPSED_TIME_SUM, ELAPSED_TIME_COUNT } = entry;
            const noData = isNoData(ELAPSED_TIME_SUM, ELAPSED_TIME_COUNT);
            return noData ? 0 : Math.round(ELAPSED_TIME_SUM / ELAPSED_TIME_COUNT);
        }
        case ROI_CHART_KEY.AVG_RUNTIME: {
            const { ELAPSED_TIME_SUM, ELAPSED_TIME_COUNT } = entry;
            const noData = isNoData(ELAPSED_TIME_SUM, ELAPSED_TIME_COUNT);
            return noData ? 0 : Math.round(ELAPSED_TIME_SUM / ELAPSED_TIME_COUNT);
        }
        case ROI_CHART_KEY.AVG_RECORDS_WRITTEN: {
            const { WRITTEN_RECORDS_SUM, WRITTEN_RECORDS_COUNT } = entry;
            const noData = isNoData(WRITTEN_RECORDS_SUM, WRITTEN_RECORDS_COUNT);
            return noData ? 0 : Math.round(WRITTEN_RECORDS_SUM / WRITTEN_RECORDS_COUNT);
        }
        default:
            return null;
    }
};

export const getTableScore = (allMetrics) => {
    const [read, write] = partition({ ACCESS_TYPE: 'READ' }, allMetrics);
    if (isEmpty(read)) {
        return {};
    }
    const queryMetrics = getAggregatedDataByBin(read, DateBin.WEEKDAY);
    const jobMetrics = getAggregatedDataByBin(write, DateBin.WEEKDAY);

    const avgQueryTimes = queryMetrics.map((entry) =>
        getMetric(ROI_CHART_KEY.AVG_QUERY_TIME, entry)
    );
    const avgQueryCounts = queryMetrics.map((entry) =>
        getMetric(ROI_CHART_KEY.AVG_QUERY_COUNT, entry)
    );

    const maxReadTime = Math.max(...avgQueryTimes);
    const maxQueryCount = Math.max(...avgQueryCounts);

    const queryTimeVsCountScore = Math.ceil(100 * (maxQueryCount / maxReadTime));
    const queryTimeToCountPercentageRatio = Math.ceil(maxReadTime / (maxReadTime + maxQueryCount));

    let writeToReadPercentageRatio = null;
    let writeVsReadScore = null;
    if (!isEmpty(jobMetrics)) {
        const avgWriteTime = jobMetrics.map((entry) => getMetric(ROI_CHART_KEY.AVG_RUNTIME, entry));
        const maxWriteTime = Math.max(...avgWriteTime);

        writeToReadPercentageRatio = Math.ceil(maxWriteTime / (maxWriteTime + maxReadTime));
        writeVsReadScore = Math.ceil(100 * (maxReadTime / maxWriteTime));
    }
    return {
        writeToReadPercentageRatio,
        queryTimeToCountPercentageRatio,
        writeVsReadScore,
        queryTimeVsCountScore
    };
};

export const getMetricsByTableFqn = (metrics) => {
    if (isEmpty(metrics)) {
        return {};
    }
    const result = {};
    metrics.forEach((record) => {
        if (!result.hasOwnProperty(record.TABLE_FQN)) {
            result[record.TABLE_FQN] = [];
        }
        result[record.TABLE_FQN].push(record);
    });
    return result;
};

export const transformMetricsResponse = (results) => {
    if (!results) {
        return null;
    }
    if (isEmpty(results.columns)) {
        return [];
    }
    return transformSnowflakeResponse({
        columns: results.columnDescriptors,
        valuesByColumn: results.columns
    });
};

export const getDimension = (data, bin) => {
    const columnDescriptors = data?.columnDescriptors;
    let dimensions = [];
    if (bin === DateBin.WEEKDAY) {
        dimensions = [{ name: DateBin.WEEKDAY, type: 'STRING', index: 0 }];
    } else if (!columnDescriptors) {
        return [];
    } else {
        dimensions = [
            {
                name: columnDescriptors && columnDescriptors[0].name,
                type: columnDescriptors && columnDescriptors[0].typeFamily,
                index: 0,
                bin
            }
        ];
    }

    if (data?.columnDescriptors.length === 3) {
        dimensions.push({
            name: data.columnDescriptors[1].name,
            type: data.columnDescriptors[1].typeFamily,
            index: 1
        });
    }
    return dimensions;
};

export const nextAggregationLevel = {
    YEAR: DateBin.YEAR,
    QUARTER: DateBin.YEAR,
    MONTH: DateBin.QUARTER,
    WEEK: DateBin.MONTH,
    DAY: DateBin.WEEK,
    WEEKDAY: DateBin.DAY,
    HOUR: DateBin.DAY
};

const getInterval = (bin, unit = 1) => {
    switch (bin) {
        case DateBin.YEAR:
            return { years: 1 };
        case DateBin.QUARTER:
            return { months: 3 };
        case DateBin.MONTH:
            return { months: 1 };
        case DateBin.WEEK:
            return { weeks: unit };
        case DateBin.DAY:
            return { days: 1 };
        default:
            return null;
    }
};
export function getSparklineTimeWindows([startDate, endDate], bin, units = 1) {
    const windows = [];
    let windowStart = startDate.startOf(bin.toLowerCase());
    if (!(startDate instanceof DateTime) || !(endDate instanceof DateTime)) {
        return [];
    }
    while (windowStart <= endDate) {
        const windowEnd =
            units === 1
                ? windowStart.endOf(bin.toLowerCase())
                : windowStart.plus(getInterval(bin, units - 1)).endOf(bin.toLowerCase());
        const nextSectionStart = windowStart.plus(getInterval(bin, units));

        const binWindow = {
            from: windowStart,
            to: windowEnd,
            year: windowStart.year,
            ...(bin === DateBin.QUARTER && { quarter: windowStart.quarter }),
            ...(bin === DateBin.MONTH && { month: windowStart.month }),
            ...(bin === DateBin.WEEK && { weekNumber: windowStart.weekNumber, units })
        };
        windows.push(binWindow);
        windowStart = nextSectionStart.startOf(bin.toLowerCase());
        if (!(nextSectionStart instanceof DateTime)) {
            break;
        }
    }

    return windows;
}

export function padChartMetricsArray(chartData, timeWindow, bin) {
    if (bin === DateBin.WEEKDAY) {
        return chartData;
    }
    if (isEmpty(chartData)) {
        return [
            [timeWindow.from, 0],
            [timeWindow.to, 0]
        ];
    }
    const firstItem = chartData[0][0].startOf(bin.toLowerCase());
    const lastItem = chartData[chartData.length - 1][0].startOf(bin.toLowerCase());
    if (timeWindow.to && lastItem < timeWindow.to) {
        if (timeWindow.to > DateTime.utc()) {
            chartData.push([DateTime.utc(), 0]);
        } else {
            chartData.push([timeWindow.to, 0]);
        }
    }
    if (timeWindow.from && firstItem > timeWindow.from) {
        chartData.unshift([timeWindow.from, 0]);
    }
    return chartData;
}

export const getMeasure = (data) => {
    if (isEmpty(data)) {
        return null;
    }

    return [
        {
            name: data.columnDescriptors[1].name,
            type: data.columnDescriptors[1].typeFamily,
            index: data.columnDescriptors.length - 1,
            aggregation: 'sum'
        }
    ];
};

const quoteTableFqn = (items) =>
    items.map((item) => {
        const { database, schema, table } = splitFqn(item);
        return `"${database}"."${schema}"."${table}"`;
    });

export const getExplorationModel = ({ dateFilters, stringFilters, arrayFilter, chartFilter }) => {
    const model = {
        filters: []
    };

    const { rangeFrom, rangeTo } = chartFilter;
    if (rangeFrom || dateFilters?.rangeFrom) {
        model.filters.push({
            column: { name: 'START_TIME', type: 'TIMESTAMP' },
            operator: { name: OPS.GTE },
            value: { name: rangeFrom ?? dateFilters?.rangeFrom, isLiteral: true }
        });
    }
    if (rangeTo || dateFilters?.rangeTo) {
        model.filters.push({
            column: { name: 'END_TIME', type: 'TIMESTAMP' },
            operator: { name: OPS.LTE },
            value: { name: rangeTo ?? dateFilters?.rangeTo, isLiteral: true }
        });
    }

    stringFilters.forEach(({ name, filteredValues, columnName }) => {
        if (!isEmpty(chartFilter[name]) || !isEmpty(filteredValues)) {
            model.filters.push({
                column: { name: columnName, type: 'STRING' },
                operator: { name: OPS.IN },
                value: chartFilter[name] ?? filteredValues
            });
        }
    });

    if (arrayFilter) {
        const filteredTables = chartFilter.tables ?? arrayFilter?.filteredValues;
        const { columnName, type } = arrayFilter;
        const hasTableFilter = !isEmpty(filteredTables);

        model.filters.push({
            column: { name: columnName, type },
            operator: { name: hasTableFilter ? OPS.ARRAYS_OVERLAP : OPS.IS_NOT_NULL },
            value: hasTableFilter && quoteTableFqn(filteredTables)
        });
    }
    return model;
};
